// status-chip.tsx — the one way status is rendered anywhere in Embent (handoff §Status
// chip; replaces the ad-hoc bg-blue-100/text-blue-800 classes that used to live in
// BotSources' statusBadge()). Pill + 6px dot + 12px/500 label. Text and dot take the
// status hue (--st-*); the background is ALWAYS the same hue washed to --st-wash — never
// a solid fill, so a wall of chips stays calm. Crawling/indexing/deleting dots bounce (the
// page is genuinely working); the animation dies under prefers-reduced-motion (globals.css).
import * as React from 'react';

import { cn } from '../lib/utils.ts';

export type StatusChipState =
  | 'pending'
  | 'crawling'
  | 'indexing'
  | 'ready'
  | 'error'
  | 'paused'
  /** Source `error` rows whose message starts with `hint:` — advisory, amber, not red. */
  | 'hint'
  /** `SourceStatus`'s teardown member (source-detail-diagnostics spec §Dashboard) — hue
   * `--st-pending` (nothing is wrong; work is in progress, same as `pending`), animated like
   * `crawling`/`indexing` so a torn-down source doesn't read as identical to a merely-paused
   * one with no motion. NOT terminal (fix round 1, Task 4 review, Important 3, corrected in the
   * whole-branch final review — this comment used to claim otherwise): a teardown job that
   * exhausts its attempts transitions `deleting → error` (`SOURCE_STATUS_TRANSITIONS`,
   * `@repo/shared-types/limits.ts`) rather than sitting stuck forever, so a `deleting` row can
   * still resolve to `error`. */
  | 'deleting';

const STATE_VAR: Record<StatusChipState, string> = {
  pending: '--st-pending',
  crawling: '--st-crawling',
  indexing: '--st-indexing',
  ready: '--st-ready',
  error: '--st-error',
  paused: '--st-paused',
  hint: '--st-paused',
  deleting: '--st-pending',
};

const ANIMATED: ReadonlySet<StatusChipState> = new Set(['crawling', 'indexing', 'deleting']);

function StatusChip({
  state,
  label,
  className,
  ...props
}: React.ComponentProps<'span'> & { state: StatusChipState; label?: string }) {
  const v = STATE_VAR[state];
  return (
    <span
      data-slot="status-chip"
      className={cn(
        'inline-flex w-fit shrink-0 items-center gap-1.5 rounded-full px-2.5 py-[3px] text-xs font-medium whitespace-nowrap',
        className,
      )}
      style={{
        color: `var(${v})`,
        backgroundColor: `color-mix(in srgb, var(${v}) var(--st-wash), transparent)`,
      }}
      {...props}
    >
      <span
        aria-hidden
        className={cn('size-1.5 rounded-full', ANIMATED.has(state) && 'st-dot-active')}
        style={{ backgroundColor: `var(${v})` }}
      />
      {label ?? state}
    </span>
  );
}

export { StatusChip };
